import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Trash2, RefreshCw, ShieldAlert } from "lucide-react";

const OPTIONS = [
  { key: "candidates",    label: "Candidatos",       hint: "Elimina também prémios associados" },
  { key: "vacancies",     label: "Vagas",            hint: "" },
  { key: "bonuses",       label: "Prémios",          hint: "" },
  { key: "notifications", label: "Notificações",     hint: "" },
  { key: "publications",  label: "Publicações",      hint: "" },
  { key: "audit",         label: "Auditoria",        hint: "" },
  { key: "groups",        label: "Grupos oficiais",  hint: "" },
  { key: "invitations",   label: "Convites",         hint: "Preserva CARING2026 (repõe contador)" },
  { key: "referrers",     label: "Referenciadores",  hint: "Elimina contas ligadas + candidatos + prémios" },
];

export default function AdminDataReset() {
  const [counts, setCounts] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/data/counts").then(({ data }) => setCounts(data));
  useEffect(() => { load(); }, []);

  const toggle = (k) => {
    const s = new Set(selected);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSelected(s);
  };

  const selectAll = () => setSelected(new Set(OPTIONS.map((o) => o.key)));
  const clear = () => setSelected(new Set());

  const run = async () => {
    if (selected.size === 0) { toast.error("Escolha pelo menos uma coleção"); return; }
    if (confirm !== "APAGAR") { toast.error('Escreva "APAGAR" no campo de confirmação'); return; }
    if (!window.confirm(`Vai eliminar definitivamente: ${[...selected].join(", ")}. Confirmar?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/data/reset", { collections: [...selected], confirm });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + Number(b || 0), 0);
      toast.success(`${total} registos eliminados`);
      setSelected(new Set()); setConfirm("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Manutenção</div>
        <h1 className="font-heading text-5xl mt-2 flex items-center gap-3">
          <ShieldAlert className="w-8 h-8 text-[var(--terracotta)]"/> Limpeza de dados de teste
        </h1>
        <p className="text-[var(--ink-soft)] mt-2 max-w-xl">
          Elimina definitivamente registos das coleções selecionadas. O utilizador administrador,
          o código de convite <span className="font-mono">CARING2026</span> e as definições ficam preservados.
        </p>
      </div>

      {counts && (
        <div className="card-surface p-6">
          <div className="eyebrow">Estado atual</div>
          <div className="mt-3 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(counts).map(([k, v]) => (
              <div key={k} className="p-3 rounded-xl bg-[var(--muted)]">
                <div className="text-xs text-[var(--ink-soft)] capitalize">{k}</div>
                <div className="font-heading text-2xl text-[var(--ink)]">{v}</div>
              </div>
            ))}
          </div>
          <button onClick={load} className="pill-btn pill-ghost !py-2 !px-4 mt-4" data-testid="refresh-counts">
            <RefreshCw className="w-4 h-4"/> Atualizar contagens
          </button>
        </div>
      )}

      <div className="card-surface p-6 border-2 border-[rgba(217,108,74,0.4)]">
        <div className="flex items-center gap-2 text-[var(--terracotta)]">
          <AlertTriangle className="w-5 h-5"/>
          <div className="font-heading text-2xl">Zona de perigo</div>
        </div>
        <p className="text-sm text-[var(--ink-soft)] mt-2">Selecione as coleções a limpar. Ação irreversível.</p>

        <div className="mt-4 grid sm:grid-cols-2 gap-2">
          {OPTIONS.map((o) => {
            const on = selected.has(o.key);
            return (
              <button
                key={o.key}
                onClick={() => toggle(o.key)}
                type="button"
                className={`text-left p-4 rounded-2xl border transition-all ${on ? "border-[var(--terracotta)] bg-[rgba(217,108,74,0.08)]" : "border-[var(--border)] bg-white hover:bg-[var(--muted)]"}`}
                data-testid={`reset-opt-${o.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-md border-2 grid place-items-center ${on ? "bg-[var(--terracotta)] border-[var(--terracotta)]" : "border-[var(--border)]"}`}>
                    {on && <span className="text-white text-xs">✓</span>}
                  </div>
                  <div>
                    <div className="font-semibold text-[var(--ink)]">{o.label}</div>
                    {o.hint && <div className="text-xs text-[var(--ink-soft)] mt-0.5">{o.hint}</div>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex gap-2">
          <button onClick={selectAll} className="pill-btn pill-ghost !py-2 !px-3" data-testid="select-all">Selecionar tudo</button>
          <button onClick={clear} className="pill-btn pill-ghost !py-2 !px-3" data-testid="clear-selection">Limpar</button>
        </div>

        <div className="mt-6 pt-6 border-t border-[var(--border)]">
          <label className="text-sm font-medium text-[var(--ink)]">
            Escreva <span className="font-mono text-[var(--terracotta)]">APAGAR</span> para confirmar
          </label>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value.toUpperCase())}
            className="mt-1.5 w-full max-w-xs px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--terracotta)] font-mono tracking-widest"
            data-testid="reset-confirm-input"
            placeholder="APAGAR"
          />
          <button
            onClick={run}
            disabled={busy || confirm !== "APAGAR" || selected.size === 0}
            className="pill-btn pill-accent mt-4"
            data-testid="reset-run-btn"
          >
            <Trash2 className="w-4 h-4"/> {busy ? "A eliminar…" : `Eliminar ${selected.size} coleção${selected.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
